import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { PLANS } from "@/lib/plans";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    Search, Shield, ShieldCheck, Users, Trash2, ChevronLeft, ChevronRight,
    Mail, MapPin, Calendar, Eye, X, Receipt, RefreshCw, Target, CandlestickChart,
    Inbox, Circle, CheckCircle2, Clock3,
} from "lucide-react";

const PAGE_SIZE = 25;

const PLAN_STYLES = {
    free: "bg-slate-100 text-slate-600",
    pro: "bg-indigo-100 text-indigo-700",
    family: "bg-amber-100 text-amber-700",
};

function PlanBadge({ plan }) {
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${PLAN_STYLES[plan] || PLAN_STYLES.free}`}>
            {PLANS[plan]?.label || "Free"}
        </span>
    );
}

function RoleBadge({ role }) {
    return role === "admin" ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
            <ShieldCheck className="w-3 h-3" /> Admin
        </span>
    ) : (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">User</span>
    );
}

// Compact <select> styled to match the app's rounded-xl inputs — used inline in the
// table for quick plan/role changes without opening the detail dialog.
function InlineSelect({ value, onChange, disabled, options }) {
    return (
        <select
            value={value} disabled={disabled}
            onChange={e => onChange(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white text-xs font-medium px-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

// ---------------- Contact Messages (see server.py's /api/admin/contact-messages) ----------------
const CONTACT_CATEGORY_LABELS = {
    general: "General Question",
    technical: "Technical Issue",
    billing: "Billing & Plans",
    feature: "Feature Request",
    partnership: "Partnership",
    press: "Press / Media",
    other: "Other",
};

const CONTACT_STATUS_STYLES = {
    new: "bg-indigo-100 text-indigo-700",
    read: "bg-slate-100 text-slate-600",
    resolved: "bg-emerald-100 text-emerald-700",
};

const CONTACT_STATUS_ICONS = { new: Circle, read: Clock3, resolved: CheckCircle2 };

function ContactStatusBadge({ status }) {
    const Icon = CONTACT_STATUS_ICONS[status] || Circle;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${CONTACT_STATUS_STYLES[status] || CONTACT_STATUS_STYLES.new}`}>
            <Icon className="w-3 h-3" /> {status}
        </span>
    );
}

function ContactCategoryBadge({ category }) {
    return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
            {CONTACT_CATEGORY_LABELS[category] || "Other"}
        </span>
    );
}

function ContactMessagesPanel() {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, unread: 0, page: 1, page_size: PAGE_SIZE });
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch) params.search = debouncedSearch;
            if (statusFilter) params.status = statusFilter;
            const { data } = await api.get("/admin/contact-messages", { params });
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load messages");
        } finally { setLoading(false); }
    }, [page, debouncedSearch, statusFilter]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (msg) => {
        setDetail(msg);
        // Opening a "new" message marks it read — mirrors any typical inbox.
        if (msg.status === "new") {
            try {
                const { data: updated } = await api.patch(`/admin/contact-messages/${msg.id}`, { status: "read" });
                setData(d => ({
                    ...d, unread: Math.max(0, d.unread - 1),
                    items: d.items.map(m => m.id === msg.id ? { ...m, ...updated } : m),
                }));
                setDetail(d => (d && d.id === msg.id ? { ...d, ...updated } : d));
            } catch { /* non-fatal — detail view already shows the message either way */ }
        }
    };

    const updateStatus = async (mid, status) => {
        setBusyId(mid);
        try {
            const { data: updated } = await api.patch(`/admin/contact-messages/${mid}`, { status });
            setData(d => ({
                ...d,
                unread: d.items.find(m => m.id === mid)?.status === "new" && status !== "new" ? Math.max(0, d.unread - 1) : d.unread,
                items: d.items.map(m => m.id === mid ? { ...m, ...updated } : m),
            }));
            setDetail(d => (d && d.id === mid ? { ...d, ...updated } : d));
            toast.success("Message updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update message");
        } finally { setBusyId(null); }
    };

    const deleteMessage = async () => {
        if (!deleteTarget) return;
        setBusyId(deleteTarget.id);
        try {
            await api.delete(`/admin/contact-messages/${deleteTarget.id}`);
            toast.success("Message deleted");
            setData(d => ({
                ...d,
                items: d.items.filter(m => m.id !== deleteTarget.id),
                total: Math.max(0, d.total - 1),
                unread: deleteTarget.status === "new" ? Math.max(0, d.unread - 1) : d.unread,
            }));
            if (detail?.id === deleteTarget.id) setDetail(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete message");
        } finally { setBusyId(null); setDeleteTarget(null); }
    };

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div className="space-y-4" data-testid="admin-messages-panel">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <div className="relative max-w-sm w-full">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or subject…"
                        data-testid="admin-messages-search" className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                    />
                </div>
                <select
                    value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="h-10 rounded-xl border border-slate-200 bg-white text-sm font-medium px-3"
                >
                    <option value="">All statuses</option>
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="resolved">Resolved</option>
                </select>
            </div>

            <div className="card-elev rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-messages-table">
                        <thead>
                            <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-5 py-3">From</th>
                                <th className="px-5 py-3">Category</th>
                                <th className="px-5 py-3">Subject</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Received</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
                            ) : data.items.length === 0 ? (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No messages found.</td></tr>
                            ) : data.items.map(m => (
                                <tr key={m.id} data-testid={`admin-message-row-${m.id}`} className={`border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer ${m.status === "new" ? "bg-indigo-50/30" : ""}`} onClick={() => openDetail(m)}>
                                    <td className="px-5 py-3">
                                        <div className="font-semibold text-slate-900">{m.name}</div>
                                        <div className="text-xs text-slate-500">{m.email}</div>
                                    </td>
                                    <td className="px-5 py-3"><ContactCategoryBadge category={m.category} /></td>
                                    <td className="px-5 py-3 max-w-xs truncate text-slate-700">{m.subject}</td>
                                    <td className="px-5 py-3"><ContactStatusBadge status={m.status} /></td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(m.created_at?.slice(0, 10))}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button onClick={() => openDetail(m)} data-testid={`admin-message-view-${m.id}`}
                                                className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(m)} data-testid={`admin-message-delete-${m.id}`}
                                                disabled={busyId === m.id}
                                                className="w-8 h-8 rounded-lg grid place-items-center text-rose-500 hover:bg-rose-50 disabled:opacity-30 transition-colors"
                                                title="Delete message"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
                        <span className="text-xs text-slate-500">Page {data.page} of {totalPages}</span>
                        <div className="flex gap-1.5">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="admin-messages-prev-page"
                                className="w-8 h-8 rounded-lg grid place-items-center border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-colors">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="admin-messages-next-page"
                                className="w-8 h-8 rounded-lg grid place-items-center border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-colors">
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="admin-message-detail">
                    <DialogHeader><DialogTitle className="font-display text-xl">{detail?.subject}</DialogTitle></DialogHeader>
                    {detail && (
                        <div className="space-y-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-display text-base font-bold text-slate-900">{detail.name}</div>
                                    <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5"><Mail className="w-3.5 h-3.5" /> {detail.email}</div>
                                </div>
                                <ContactCategoryBadge category={detail.category} />
                            </div>

                            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {detail.message}
                            </div>

                            <div className="flex items-center justify-between text-xs text-slate-400">
                                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(detail.created_at?.slice(0, 10))}</span>
                                <a href={`mailto:${detail.email}?subject=${encodeURIComponent("Re: " + detail.subject)}`} className="text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1">
                                    <Mail className="w-3.5 h-3.5" /> Reply by email
                                </a>
                            </div>

                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Status</p>
                                <div className="flex gap-2">
                                    {["new", "read", "resolved"].map(s => (
                                        <button
                                            key={s} onClick={() => updateStatus(detail.id, s)} disabled={busyId === detail.id || detail.status === s}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border transition-colors ${detail.status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"} disabled:cursor-default`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-xl">Delete this message?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently deletes the message from {deleteTarget?.name}. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteMessage} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                            Delete message
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ---------------- Users panel (original Admin content) ----------------
function AdminUsersPanel() {
    const { user: me } = useAuth();
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
    const [loading, setLoading] = useState(true);
    const [detailId, setDetailId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch) params.search = debouncedSearch;
            const { data } = await api.get("/admin/users", { params });
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load users");
        } finally { setLoading(false); }
    }, [page, debouncedSearch]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (uid) => {
        setDetailId(uid);
        setDetail(null);
        setDetailLoading(true);
        try {
            const { data } = await api.get(`/admin/users/${uid}`);
            setDetail(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load user");
            setDetailId(null);
        } finally { setDetailLoading(false); }
    };

    const updateUser = async (uid, payload) => {
        setBusyId(uid);
        try {
            const { data: updated } = await api.patch(`/admin/users/${uid}`, payload);
            setData(d => ({ ...d, items: d.items.map(u => u.id === uid ? { ...u, ...updated } : u) }));
            setDetail(d => (d && d.id === uid ? { ...d, ...updated } : d));
            toast.success("User updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update user");
        } finally { setBusyId(null); }
    };

    const deleteUser = async () => {
        if (!deleteTarget) return;
        setBusyId(deleteTarget.id);
        try {
            await api.delete(`/admin/users/${deleteTarget.id}`);
            toast.success("User deleted");
            setData(d => ({ ...d, items: d.items.filter(u => u.id !== deleteTarget.id), total: Math.max(0, d.total - 1) }));
            if (detailId === deleteTarget.id) setDetailId(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete user");
        } finally { setBusyId(null); setDeleteTarget(null); }
    };

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div className="space-y-4" data-testid="admin-users-panel">
            <p className="text-sm text-slate-500">{data.total} user{data.total === 1 ? "" : "s"} — view details, change plans, and manage roles.</p>

            <div className="relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                    value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
                    data-testid="admin-search" className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                />
            </div>

            <div className="card-elev rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                        <thead>
                            <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-5 py-3">User</th>
                                <th className="px-5 py-3">Plan</th>
                                <th className="px-5 py-3">Role</th>
                                <th className="px-5 py-3">Household</th>
                                <th className="px-5 py-3">Joined</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
                            ) : data.items.length === 0 ? (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No users found.</td></tr>
                            ) : data.items.map(u => (
                                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                                    <td className="px-5 py-3">
                                        <div className="font-semibold text-slate-900">{u.name}</div>
                                        <div className="text-xs text-slate-500">{u.email}</div>
                                    </td>
                                    <td className="px-5 py-3">
                                        <InlineSelect
                                            value={u.plan} disabled={busyId === u.id}
                                            onChange={(v) => updateUser(u.id, { plan: v })}
                                            options={[{ value: "free", label: "Free" }, { value: "pro", label: "Pro" }, { value: "family", label: "Family" }]}
                                        />
                                    </td>
                                    <td className="px-5 py-3">
                                        {u.is_super_admin ? (
                                            <RoleBadge role="admin" />
                                        ) : (
                                            <InlineSelect
                                                value={u.role} disabled={busyId === u.id}
                                                onChange={(v) => updateUser(u.id, { role: v })}
                                                options={[{ value: "user", label: "User" }, { value: "admin", label: "Admin" }]}
                                            />
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        {u.household_name ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                                                <Users className="w-3.5 h-3.5 text-slate-400" />
                                                {u.household_name}{u.is_household_owner && <span className="text-slate-400">(owner)</span>}
                                            </span>
                                        ) : <span className="text-xs text-slate-300">—</span>}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(u.created_at?.slice(0, 10))}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button onClick={() => openDetail(u.id)} data-testid={`admin-view-${u.id}`}
                                                className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(u)} data-testid={`admin-delete-${u.id}`}
                                                disabled={u.is_super_admin || u.id === me?.id}
                                                className="w-8 h-8 rounded-lg grid place-items-center text-rose-500 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                                                title={u.is_super_admin ? "Permanent admin — can't be deleted" : u.id === me?.id ? "Use Settings to delete your own account" : "Delete user"}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
                        <span className="text-xs text-slate-500">Page {data.page} of {totalPages}</span>
                        <div className="flex gap-1.5">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="admin-prev-page"
                                className="w-8 h-8 rounded-lg grid place-items-center border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-colors">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="admin-next-page"
                                className="w-8 h-8 rounded-lg grid place-items-center border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-colors">
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail dialog */}
            <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
                <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="admin-user-detail">
                    <DialogHeader><DialogTitle className="font-display text-xl">User details</DialogTitle></DialogHeader>
                    {detailLoading || !detail ? (
                        <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
                    ) : (
                        <div className="space-y-5">
                            <div>
                                <div className="font-display text-lg font-bold text-slate-900">{detail.name}</div>
                                <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5"><Mail className="w-3.5 h-3.5" /> {detail.email}</div>
                                {(detail.city || detail.country) && (
                                    <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5">
                                        <MapPin className="w-3.5 h-3.5" /> {[detail.city, detail.country].filter(Boolean).join(", ")}
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5">
                                    <Calendar className="w-3.5 h-3.5" /> Joined {fmtDate(detail.created_at?.slice(0, 10))}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <PlanBadge plan={detail.plan} />
                                <RoleBadge role={detail.role} />
                            </div>

                            <div className="grid grid-cols-4 gap-2 text-center">
                                {[
                                    { icon: Receipt, label: "Expenses", value: detail.expense_count },
                                    { icon: RefreshCw, label: "Subs", value: detail.subscription_count },
                                    { icon: Target, label: "Funds", value: detail.fund_count },
                                    { icon: CandlestickChart, label: "Invest.", value: detail.investment_count },
                                ].map(({ icon: Icon, label, value }) => (
                                    <div key={label} className="rounded-xl bg-slate-50 p-3">
                                        <Icon className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                                        <div className="font-display text-lg font-bold text-slate-900 tabular-nums">{value}</div>
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
                                    </div>
                                ))}
                            </div>

                            {detail.household_name && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                        Household — {detail.household_name}
                                    </div>
                                    <div className="space-y-1.5">
                                        {detail.household_members.map(m => (
                                            <div key={m.id} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-slate-50">
                                                <span className="text-slate-700">{m.name}</span>
                                                {m.is_owner && <span className="text-xs text-slate-400">Owner</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-xl">Delete {deleteTarget?.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently deletes their account, including their Supabase Auth login, and all their expenses, subscriptions, funds, and investments. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteUser} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                            Delete user
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function Admin() {
    const [tab, setTab] = useState("users");
    const [unread, setUnread] = useState(0);

    // Poll the unread contact-message count so the tab badge stays current even while
    // the admin is sitting on the Users tab — cheap (one paginated call, page_size=1).
    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const { data } = await api.get("/admin/contact-messages", { params: { page: 1, page_size: 1, status: "new" } });
                if (!cancelled) setUnread(data.total || 0);
            } catch { /* non-fatal — badge just won't update this cycle */ }
        };
        check();
        const t = setInterval(check, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    return (
        <div className="space-y-4" data-testid="admin-page">
            <div>
                <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
                    <Shield className="w-6 h-6 text-indigo-600" /> Admin
                </h1>
            </div>

            <div className="flex items-center gap-1.5 border-b border-slate-100 pb-px" data-testid="admin-tabs">
                <button
                    onClick={() => setTab("users")} data-testid="admin-tab-users"
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${tab === "users" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                >
                    <Users className="w-4 h-4" /> Users
                </button>
                <button
                    onClick={() => setTab("messages")} data-testid="admin-tab-messages"
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${tab === "messages" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                >
                    <Inbox className="w-4 h-4" /> Messages
                    {unread > 0 && (
                        <span className="ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                            {unread > 99 ? "99+" : unread}
                        </span>
                    )}
                </button>
            </div>

            {tab === "users" ? <AdminUsersPanel /> : <ContactMessagesPanel />}
        </div>
    );
}
